import { useMemo } from 'react';
import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap, CircleMarker } from 'react-leaflet';
import { Dialog, DialogTitle, DialogContent, Button, DialogContentText, DialogActions, IconButton, Paper, Typography, Switch, Stack, TextField, Chip  } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import networkData from '../../../sampledata/SampleNetwork';
import { useNetwork, useSection, updateLocationParameters, useStored, updateSelectedSection, useStoredParameters, useStoredLocationParams, useStoredLocationData, useInputParams, getInputParamsFromApi, updateTags, DeleteTags, useTags, useAllTags, useSearchMap, useIsSearchActive, useParamFilterState, updateFilter, useBucklingStateSchema, useResponseSchema } from '../../../state/services';
import { useInputParamState } from '../../../state/Input-param-state';
import { useOktaAuth } from '@okta/okta-react';
import { QueryStats, Close, HelpOutlineOutlined, Recommend, ExpandMore, ExpandLess, AddCircleOutline } from '@mui/icons-material';
import { SectionData } from '../../../api-request/api-request';
import { Box } from "@mui/material";
import { createRoot } from "react-dom/client";
import MarkerClusterGroup from "react-leaflet-cluster";
import { AlertDialogSlide } from '../../../components/SidePanelTab';
import "leaflet.markercluster";
import { useAppPermanentState } from '../../../state/dynamic-perm-state';
import { BucklingStatus, DynamicInputParameters, Parameter, ParameterGroup, ResponseSchema } from '../../../types/ApiData';
import DynamicInputParameter from '../right-screen/DynamicInputParameter';
import { applySetValueRules, buildDefaultsFromSchema, buildDependencyGraphs, buildRulesMap, buildSetValueDependencyGraph, FormValues, getBucklingColor, getMarker, getNetworkName, getNetworkNameForOnlineMaps, getStatuColor, validateField, ValidationMessage } from '../../../utils/utils';
import { greyIcon, activeGreyIcon } from '../../../components/MarkerIcon';
import ReusableDialog from '../../../components/Dialog';
import { DeleteForever, FilterAltOutlined, Clear } from '@mui/icons-material';
import { newId, ParamFilterDialog, ParamFilterState } from './GlobalFilter';
import { useCoordPassSet } from '../../../hooks/useCoordPassSet';
import { hasActiveFilter } from '../../../utils/filterUtils';
import { useMode, useOnlineNetworks, useSetSelectedMarker } from '../../../state/application-state-service';
import { GeoJSONMap } from './GeoJsonMap';
import { buildValuesFromMetadata } from '../../../utils/onlineMapsUtils';

const HeatMap = ({ isRightSidebarVisible }: { isRightSidebarVisible: boolean }) => {
    const network = useNetwork()
    const section = useSection()
    const bucklingData = useStoredLocationData()
    const storedData = useStored()
    const paramState = useInputParamState()
    const storedInputParams = useInputParams()
    const searchMap = useSearchMap()
    const isSearchActive = useIsSearchActive()
    const savedGlobalParams = useStoredParameters() as DynamicInputParameters[]
    const storedLocationParams = useStoredLocationParams()
    const paramFilter = useParamFilterState()
    const { coordPassSet } = useCoordPassSet({
        network,
        savedGlobalParams,
        storedLocationParams,
        paramFilter,
        networkId: network.id ?? ''
    });
    const bucklingSchema = useBucklingStateSchema()
    const responseSchema = useResponseSchema()

    const [formOpen, setFormOpen] = useState(false)
    const [selectedId, setSelectedId] = useState({})
    const [showMarkers, setShowMarkers] = useState<boolean>(false)
    const [popupOpen, setPopupOpen] = useState(false);
    const { authState } = useOktaAuth();
    const accessToken = authState?.accessToken?.accessToken || '';
    const [tagsPopupOpen, setTagsPopupOpen] = useState(false)
    const [filterOpen, setFilterOpen] = useState(false);
    const mode = useMode()
    const setSelectedMarker = useSetSelectedMarker()

    const handleCloseFilter = () => {
        setFilterOpen(false)
    }

    const handleResetFilter = () => {
        updateFilter(paramState, { logic: "AND", groups: [ { id: newId(), logic: "AND", rows: [] }]})
        if(mode === 'offline') updateSelectedSection(paramState, [])
        else setSelectedMarker('')    
    }

    const handleApplyFilter = (param: ParamFilterState) => {
        updateFilter(paramState, param)
         updateSelectedSection(paramState, [])
    }

    const allParams: Parameter[] = useMemo(
        () =>
            Array.isArray(storedInputParams)
                ? storedInputParams.flatMap(g => g.parameters)
                : [],
        [storedInputParams]
    );


    const setMarkerVisibility = () => {
        setShowMarkers(prev => !prev)
        updateSelectedSection(paramState, [])
        if(mode == 'online') setSelectedMarker('')
    }
    
    const displayForm = async (coord: any) => {
        setSelectedId({ ...selectedId, coord: coord })
              if(storedInputParams.length === 0)
                await getInputParamsFromApi(paramState,accessToken)
        setFormOpen(true)
    }

    const displayPopup = (coord: any) => {
        setSelectedId({ ...selectedId, coord: coord })
        setPopupOpen(true)
    }

    const closeForm = () => {
        setFormOpen(false)
    }

    useEffect(() => {
        if (section && section.railwayEdgeId) {
            const selectedEdge = network.edgesOfNetwork?.find(
                (edge) => edge.railwayEdgeId === section.railwayEdgeId
            );
            setSelectedId({ name: selectedEdge?.railwayEdgeName, id: selectedEdge?.railwayEdgeId })
        }
    }, [section])

    useEffect(() => {
        if (section && section.coordinates) {
            if(showMarkers === false) setShowMarkers(true)
        }
    }, [section.coordinates])

    const mapRef = useRef<L.Map | null>(null); 

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.invalidateSize(); 
        }
    }, [isRightSidebarVisible]);


    // Extract the center from the network data
    const center: [number, number] = [
        networkData.network[0].centerGeoPoint.coordinates[1], // Latitude
        networkData.network[0].centerGeoPoint.coordinates[0]  // Longitude
    ];

    const polylines = network.edgesOfNetwork?.map((edge) =>
        edge.lineString?.coordinates?.map(([lng, lat]: [number, number]) => [lat, lng] as [number, number])
    );

    let commonResult: any

    if (storedData) {
        let matchingData : any
        storedData.forEach((data) => {
            if (network.id && data.id === network.id && data.checked === true) {
              matchingData = data;
            }
        });
        if (matchingData) {
            if (matchingData?.sections?.length > 0) {
                commonResult = matchingData?.sections[0]
            }
        }
    }

    const selectSection = (lat: number, long: number, edgeId : string, edgeName: string, index?: number) => {
        let values = { ...section, coordinates: [{ lat, long }], railwayEdgeId: edgeId, edgeName: edgeName, index: index ?? 0 }
        updateSelectedSection(paramState, values)
    }

    const passesParamFilter = (lat: number, lng: number) => {
        if (!hasActiveFilter(paramFilter)) return true;   // no active param filter
        if (!coordPassSet) return true;                   // safety / not computed yet
        return coordPassSet.has(`${lat},${lng}`);
    };

    const shouldRenderMarker = (edgeId: string, lat: number, lng: number) => {
        // 1) tag search behavior (unchanged)
        if (isSearchActive) {
            const edgeSearch = searchMap[edgeId];
            if (!edgeSearch) return false;
            if (!edgeSearch.edgeMatch && !edgeSearch.coordMatches.has(`${lat}-${lng}`)) return false;
        }

        // 2) param filter (groups)
        if (!passesParamFilter(lat, lng)) return false;

        return true;
    };


    const markers = useMemo(() => {
        if (!network?.edgesOfNetwork) return [];

        return network.edgesOfNetwork?.flatMap((polyline, polyIndex: number) => {
            return polyline?.lineString?.coordinates.filter(([lng, lat]: [number, number]) =>
                shouldRenderMarker(polyline.railwayEdgeId, lat, lng)
            ).map(([lng, lat]: [number, number], index: number) => {
                let coord = [lat, lng] as [number, number]
                let flag = false
                let statusInfo = { status: 'undefined', badgeColor: '#6E6E6E', textColor: 'white', state: '', icon: '' }
                let iconInfo = { icon: greyIcon, activeIcon: activeGreyIcon }
                let icon = iconInfo.icon

                if (typeof bucklingData[`${coord[0]},${coord[1]}`] === "object" &&
                    bucklingData[`${coord[0]},${coord[1]}`] !== null &&
                    Object.keys(bucklingData[`${coord[0]},${coord[1]}`] as object).length > 0) {
                    let response = bucklingData[`${coord[0]},${coord[1]}`] as SectionData
                    statusInfo = getStatuColor(response.buckling_status, bucklingSchema)
                }
                iconInfo = getMarker(statusInfo.icon)
                icon = iconInfo.icon

                if (section.coordinates && (section.coordinates[0]?.lat === coord[0] && section.coordinates[0]?.long === coord[1])) {
                    flag = true
                }

                if (flag) icon = iconInfo.activeIcon
                if (section && section.coordinates && section.coordinates.length > 0 && coord[0] === section?.coordinates[0].lat && coord[1] === section?.coordinates[0].long) {
                    return <Marker key={`${polyIndex}-${index}`} position={[section?.coordinates[0].lat, section?.coordinates[0].long]}
                        icon={icon}
                        eventHandlers={{
                            click: (e) => {
                                selectSection(coord[0], coord[1], polyline.railwayEdgeId, polyline.railwayEdgeName, index)
                            }
                        }}>
                    </Marker>
                }
                return <CircleMarker radius={5} key={`${polyIndex}-${index}`} center={coord}
                    pathOptions={{
                        color: statusInfo.badgeColor,
                        fillColor: statusInfo.badgeColor,
                        fillOpacity: 1
                    }}
                    eventHandlers={{
                        click: (e) => {
                            selectSection(coord[0], coord[1], polyline.railwayEdgeId, polyline.railwayEdgeName, index)
                        }
                    }}>
                </CircleMarker>
            });
        })
    }, [
        network?.edgesOfNetwork,
        searchMap,
        isSearchActive,
        section,
        bucklingData,
        //commonResult,
        coordPassSet
    ]);
    
    const FitBounds = () => {
        const map = useMap(); // Use the map context
        let coordinates = polylines?.flat(); // Flatten the polyline coordinates
        if (!coordinates || coordinates.length === 0) {
            return null; // If coordinates are undefined or empty, return early
        }

        if (section && section.coordinates?.length > 0) {
                coordinates = [[section.coordinates[0].lat,section.coordinates[0].long]]
         } 

        const bounds = new L.LatLngBounds(coordinates); // Create a bounds object from the coordinates
        map.fitBounds(bounds, { padding: [50, 50] });// Fit the map to the bounds
        return null; // This component does not render anything
    }; 

    const getTrackColor = (coord: [number, number], edgeId: string) => {
        let bucklingStatus = { status: 0, color: '#6E6E6E' } // status 0:1:2 not calculated:buckling global:buckling individual
        let buckling_status: any = ''
        const key = `${coord[0]},${coord[1]}`
        if (typeof bucklingData[key] === "object" &&
            bucklingData[key] !== null &&
            Object.keys(bucklingData[key] as object).length > 0) {
            let val = bucklingData[key] as SectionData
            //if (val.id === edgeId) {
                bucklingStatus.status = 2
                buckling_status = val.buckling_status ?? 'g1'
                bucklingStatus.color = getBucklingColor(buckling_status, bucklingSchema, 'dark')
            //}
        }
        return bucklingStatus
    }

    return (
        <>
            {mode === 'offline' ? <div style={{ position: "relative", height: "100%", width: "100%" }}>
                <MapContainer
                    center={center}
                    zoom={18}
                   style={{ height: "100%", width: "100%" }}
                    ref={mapRef}
                    renderer={L.canvas()}
                >
                    <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        maxZoom={19}
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />

                    {/* Render Polylines */}
                    {network.id &&
                        network.edgesOfNetwork?.map((polyline, index: number) => {
                            //let flag = false
                            return polyline.lineString?.coordinates?.slice(0, -1).map((_ : [number, number], i: number) => {
                                const start : [number, number] = [polyline.lineString.coordinates[i][1], polyline.lineString.coordinates[i][0]]; // Start Point
                                const end : [number, number] = [polyline.lineString.coordinates[i + 1][1], polyline.lineString.coordinates[i + 1][0]]; // End Point
                                let color = ""; 
                                let opacity = 1;
                                let weight = 5
                                let dashArrayValue = false // Dash pattern (length of dashes and gaps)
                                let bucklingStatus : {color : string, status : number} 
                                let newPointColor :string
                                let newPoint: [number, number] | undefined 
                                let segment: [number, number][] = [
                                    [polyline.lineString.coordinates[i][1], polyline.lineString.coordinates[i][0]],
                                    [polyline.lineString.coordinates[i + 1][1], polyline.lineString.coordinates[i + 1][0]]
                                    ];
                                
                                bucklingStatus = getTrackColor(start,polyline.railwayEdgeId)
                                color = bucklingStatus.color
                                newPointColor = color
                                if(bucklingStatus.status === 1) {
                                    dashArrayValue = false
                                    weight = 4
                                } else if(bucklingStatus.status === 2)  {
                                    weight = 7
                                    dashArrayValue = false
                                    newPoint = getPointAlongPath(start, end, 10);
                                    segment = [
                                        [polyline.lineString.coordinates[i][1], polyline.lineString.coordinates[i][0]],
                                        newPoint
                                        ]; 
                                        newPointColor = getTrackColor(newPoint,polyline.railwayEdgeId).color
                                }

                                return (
                                    <>
                                        <Polyline
                                            eventHandlers={{ click: (e) => {
                                                const { lat, lng } = e.latlng;
                                                selectSection(lat, lng, polyline.railwayEdgeId, polyline.railwayEdgeName, index)
                                            } }}
                                            key={`${index+1}-${i}`}
                                            positions={segment} 
                                            pathOptions={{ color, weight: weight, opacity: opacity,  dashArray: dashArrayValue ? "8,8" : undefined }}
                                        />
                                    {(bucklingStatus.status === 2 && Array.isArray(newPoint)) &&
                                        <Polyline
                                        key={`${i+1}-${index}-${i}`}
                                        positions={[newPoint,end]} 
                                        pathOptions={{ color : newPointColor, weight: 4, opacity: opacity,  dashArray: dashArrayValue ? "8,8" : undefined }}
                                    />
                                    }
                                    </>
                                );
                            });
                        })}
                  {showMarkers && network.id && <MarkerClusterGroup  spiderfyOnMaxZoom={false} chunkedLoading disableClusteringAtZoom={18}>
                    {markers}
                    </MarkerClusterGroup>} 
                     {/* {showMarkers && markers}*/}
                    <FitBounds />
                    {showMarkers && <CustomPopup responseSchema={responseSchema} bucklingSchema={bucklingSchema} setTagsPopupOpen={setTagsPopupOpen} displayPopup={displayPopup} displayForm={displayForm} section={section} commonResult={commonResult} bucklingData={bucklingData} getStatuColor={getStatuColor} />}
                    
                </MapContainer>
                <LegendMarker bucklingSchema={bucklingSchema} setFilterOpen={setFilterOpen} filterVisible={(Object.keys(storedInputParams).length > 0 && network.id) ? true: false} isReset={hasActiveFilter(paramFilter)} onReset={handleResetFilter} showMarkers={showMarkers} setmarker={setMarkerVisibility} />
            </div> : 
            <GeoJSONMap setShowMarkers={setShowMarkers} showMarkers={showMarkers}>
                <LegendMarker bucklingSchema={bucklingSchema} setFilterOpen={setFilterOpen} filterVisible={(Object.keys(storedInputParams).length > 0 && network.id) ? true: false} isReset={hasActiveFilter(paramFilter)} onReset={handleResetFilter} showMarkers={showMarkers} setmarker={setMarkerVisibility} />
            </GeoJSONMap>}
            {(Object.keys(storedInputParams).length > 0 && formOpen) && <InputDialogForm storedInputParams={storedInputParams} selectedSection={selectedId} open={formOpen} closeForm={closeForm} />}
            <RecommendationPopup commonResult={commonResult} bucklingData={bucklingData} selectedSection={selectedId} open={popupOpen} setOpen={setPopupOpen} />
            {tagsPopupOpen && <ReusableDialog open={tagsPopupOpen} cancelText='Close' title='Tag Details' content={<TagDialogDetails isGeoPoint={true} section={section}/>} onCancel={() => setTagsPopupOpen(false)} />}
            {(Object.keys(storedInputParams).length > 0 && filterOpen) && <ParamFilterDialog
                open={filterOpen}
                onClose={handleCloseFilter}
                params={allParams}
                value={paramFilter}
                onApply={handleApplyFilter}
                onReset={handleResetFilter}
            />}
        </>
    );
};


const LegendMarker = ({ setmarker, showMarkers, setFilterOpen, isReset, onReset, filterVisible, bucklingSchema }: { setmarker: () => void, showMarkers : boolean, setFilterOpen: (val: boolean) => void, isReset: boolean, onReset: () => void, filterVisible: boolean, bucklingSchema: BucklingStatus[] }) => {
    const [showPopup, setShowPopup] = useState(false);

    return (
        <>
            <Paper
                elevation={3}
                style={{
                    position: "absolute",
                    top: 80,
                    left: 20,
                    padding: "1px",
                    background: "white",
                    //borderRadius: "none",
                    zIndex: 1000,
                }}
            >
                {filterVisible && <Button  endIcon={<FilterAltOutlined />} onClick={() => setFilterOpen(true)}>Filter</Button>}
            </Paper>
            <Paper
                elevation={3}
                style={{
                    position: "absolute",
                    top: 80,
                    left: 130,
                    padding: "1px",
                    background: "white",
                    //borderRadius: "none",
                    zIndex: 1000,
                }}
            >
                {isReset && <Button color='error'  endIcon={<Clear />} onClick={() => onReset()}>Reset</Button>}
            </Paper>
            {/* Floating Switch Above */}
            <Paper
                elevation={3}
                style={{
                    position: "absolute",
                    bottom: 70, 
                    left: 20,
                    padding: "1px",
                    background: "white",
                    borderRadius: "20px",
                    zIndex: 1000,
                }}
            >
                <Switch
                    checked={showMarkers}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={setmarker}
                />
            </Paper>

            {/* Floating Button */}
            <IconButton
                onClick={() => setShowPopup(!showPopup)}
                style={{
                    position: "absolute",
                    bottom: 20,
                    left: 20,
                    background: "white",
                    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                    zIndex: 1000,
                }}
            >
                <HelpOutlineOutlined color="success" />
            </IconButton>

            {/* Popup */}
            {showPopup && (
                <Paper elevation={3} style={{ position: "absolute", bottom: 70, left: 20, padding: "10px 44px 10px 10px", pointerEvents: "auto", minWidth: "200px", zIndex: 1000, boxSizing: "border-box", }} >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1001, }} >
                        <IconButton size="small" onClick={() => setShowPopup(false)} sx={{ position: "absolute", top: 0, right: 0, width: 36, height: 36, zIndex: 1002, }} >
                            <Close fontSize="small" />
                        </IconButton>
                        <div style={{ width: "100%", minWidth: 0 }}>
                            <StyledLines bucklingSchema={bucklingSchema} />
                        </div>
                    </div>
                </Paper>
            )}
        </>
    );
};

export const RecommendationPopup = ({selectedSection, open, setOpen, commonResult, bucklingData}:{selectedSection: any, open: boolean, setOpen : (val:boolean) => void, commonResult: any, bucklingData: any}) => {
    let coord: [number, number] | null = null;
    let data

    if (selectedSection.coord && selectedSection.coord.length > 0) {
        const lat = selectedSection.coord[0];
        const long = selectedSection.coord[1];

        if (lat !== undefined && long !== undefined) {
            coord = [lat, long];
        }
    }

    if (coord)
        if (typeof bucklingData[`${coord[0]},${coord[1]}`] === "object" &&
            bucklingData[`${coord[0]},${coord[1]}`] !== null &&
            Object.keys(bucklingData[`${coord[0]},${coord[1]}`] as object).length > 0) {
            let response = bucklingData[`${coord[0]},${coord[1]}`] as SectionData
            if(response?.recommendations && (response?.recommendations).length > 0) {
                data = response?.recommendations
            }
        }
    return <>
        {data && open && <AlertDialogSlide data={data} open={open} setOpen={setOpen} />}
    </>
}

interface FormDialogProps {
    open: boolean,
    closeForm: () => void,
    selectedSection: any
    storedInputParams: ParameterGroup[]
    metadata?: Record<string, any> | null
}


export const InputDialogForm = ({ open, closeForm, selectedSection, storedInputParams, metadata = null }: FormDialogProps) => {
    const [paramsval, setParamsval] = useState<ParameterGroup[]>([]);
     const [formValues, setFormValues] = useState<FormValues>({});
    const paramState = useInputParamState()
    const network = useNetwork()
    const sections = network.edgesOfNetwork
    const locations = useStoredLocationParams()
    const [errors, setErrors] = useState<Record<string, ValidationMessage | undefined>>({});
    const storedData = useStoredParameters() as DynamicInputParameters[]
    const permanentState = useAppPermanentState();
    let coordDetails : {[key : string] : any} = {name:'', id: '', coordinates : []}
    const storedSection = useSection()
    const mode = useMode()
    const onlineNetworks = useOnlineNetworks()
    
    const params =
        network.id
            ? storedData?.find(
                (data) => data.id === network.id && data.checked
            ) ?? {}
            : {};
    

    coordDetails = mode === 'offline' ? findCoordDetails(selectedSection, sections)
                                      : {name: selectedSection.name, 
                                         id: selectedSection.id, 
                                         coordinates: [selectedSection.coord[0], 
                                         selectedSection.coord[1]]
                                        }

    const { authState } = useOktaAuth();

    const accessToken = authState?.accessToken?.accessToken || '';

    const dependencyGraph = useMemo(
        () => buildDependencyGraphs(storedInputParams),
        [storedInputParams]
    );

    const setValueDependencyGraph = useMemo(
        () => buildSetValueDependencyGraph(storedInputParams),
        [storedInputParams]
    );

    const paramsRules = useMemo(
        () =>
            storedInputParams.length > 0
                ? buildRulesMap(storedInputParams)
                : {},
        [storedInputParams]
    );
    const metadataValues = useMemo(() =>
        buildValuesFromMetadata(
            metadata,
            storedInputParams
        ), [onlineNetworks, storedInputParams]
    );

    const onlineNetworkFieldCodes = useMemo(
        () => Object.keys(metadataValues),
        [metadataValues]
    );

    const selectedLocationKey = selectedSection?.coord
        ? `${selectedSection.coord[0]},${selectedSection.coord[1]}`
        : "";

    const locationValues = selectedLocationKey
        ? locations?.[selectedLocationKey]
        : undefined;

    const hasLocations =
        Boolean(locationValues) &&
        Object.keys(locationValues ?? {}).length > 0;

    const hasParams =
        Boolean(params) &&
        Object.keys(params ?? {}).length > 0;

    const hasMetadataValues =
        Object.keys(metadataValues).length > 0;

    const externalData =
        !hasLocations &&
        hasMetadataValues;

    useEffect(() => {
        if (!open) return;
        setParamsval(storedInputParams);

        const defaults = buildDefaultsFromSchema(storedInputParams);

        if ( hasLocations && locationValues ) {
            setFormValues({ 
                ...defaults, 
                ...locationValues 
            });
        } else if(hasParams) {
            setFormValues({
                ...defaults,
                ...params,
                ...metadataValues
            });
        } else {
            setFormValues({
                ...defaults,
                ...metadataValues
            });
        }

        setErrors({});
    }, [
        open,
        storedInputParams,
        metadata,
        locationValues,
        metadataValues
    ]);


    const handleChange = (field: string, value: any) => {
        setFormValues((prev) => {
            let newValues = { ...prev, [field]: value };

            newValues = applySetValueRules(
                field,
                newValues,
                paramsRules,
                setValueDependencyGraph
            );

            const nextErrors: Record<string, ValidationMessage | undefined> = {};

            nextErrors[field] = validateField(field, value, newValues, paramsRules) ?? undefined;

            dependencyGraph[field]?.forEach(depField => {
                nextErrors[depField] =
                    validateField(depField, newValues[depField], newValues, paramsRules) ?? undefined;
            });

            setErrors(prev => ({ ...prev, ...nextErrors }));
            return newValues;
        })
    }

    const isFormValid =
        !Object.values(errors).some(err => err?.type === 1);

    const formSubmit = async () => {
        const networkDetails = mode === 'offline' ? {
            network_id: getNetworkName(network.id) ?? "unknown-network",
            coords: selectedSection
                ? {
                    latitude: selectedSection[0],
                    longitude: selectedSection[1],
                }
                : {
                    latitude: 0,
                    longitude: 0,
                },
            section_id: storedSection
                ? `${storedSection.edgeName}.${storedSection.index}`
                : "unknown",
            network_uid: network.id
        } : {
            network_id: getNetworkNameForOnlineMaps(network.id, onlineNetworks) ?? "unknown-network",
            coords: selectedSection
                ? {
                    latitude: selectedSection.coord[0],
                    longitude: selectedSection.coord[1],
                }
                : {
                    latitude: 0,
                    longitude: 0,
                },
            section_id: selectedSection.id,
            network_uid: network.id
        };

        const response = await updateLocationParameters(paramState, permanentState, { ...formValues, id: selectedSection.id }, accessToken, `${selectedSection.coord[0]},${selectedSection.coord[1]}`,coordDetails, networkDetails)
        if (response.status === "successful" && response.errors === '') {
            setFormValues(formValues)
            closeForm()
        }
    }

    let panel = paramsval.length > 0 ? paramsval[0].group_code : 'usage'

    return (
        <>
            <Dialog open={open} >
                <DialogTitle>Calculate track buckling for {selectedSection.name}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Add the input parameters to view the buckling on the map
                    </DialogContentText>
                    <div>
                        <DynamicInputParameter 
                            dynamicRules={paramsRules} 
                            panel={panel} params={paramsval} 
                            isGlobal={false}  errors={errors} 
                            formValues={formValues} 
                            handleChange={handleChange} 
                            onlineNetworkFieldCodes={
                                externalData ? onlineNetworkFieldCodes : []
                            }
                        />
                    </div>
                    <div>
                        {Object.values(errors).some(e => e?.type === 1) && (
                            <p style={{ color: 'red' }}>
                                *There are blocking errors in the form. Please fix them to continue.
                            </p>
                        )}

                        {Object.values(errors).some(e => e?.type === 2) &&
                            !Object.values(errors).some(e => e?.type === 1) && (
                                <p style={{ color: 'orange' }}>
                                    *There are warnings in the form. You may continue.
                                </p>
                            )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" endIcon={<Close />} color='error' onClick={() => {closeForm()}} >Cancel</Button>
                    <Button variant="outlined"  disabled={!isFormValid}  onClick={() => {formSubmit()}} endIcon={<QueryStats />}>Calculate</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const findCoordDetails = (selectedSection: any, sections : any) => {
    let coordDetails : {[key : string] : any} = {name:'', id: '', coordinates : []}

    if(sections && sections.length > 0) {
        sections.forEach((edge : any) => {
            edge.lineString.coordinates.forEach((coord : any, index : number) => {
                const key = `${coord[1]},${coord[0]}`; // Convert to "lat,long"              
                    if(selectedSection?.coord && key === `${selectedSection?.coord[0]},${selectedSection?.coord[1]}`) {
                        coordDetails = {name:`${edge.railwayEdgeName}.${index}`, id: edge.railwayEdgeId, coordinates : [selectedSection?.coord[0],selectedSection?.coord[1]]}
                    }
            });
        });     
    }
    return coordDetails
}

const StyledLines = ({ bucklingSchema }: { bucklingSchema: BucklingStatus[] }) => {
    return (
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} mt={5}>
            <Box display="flex" alignItems="center">
                <Box sx={{ width: "50px", borderBottom: "2px solid #6E6E6E" }} />
                <Typography variant="body2" color="#6E6E6E" ml={1}>
                    Unmeasured Track
                </Typography>
            </Box>
            {bucklingSchema && bucklingSchema.length > 0 && bucklingSchema.map((item) => {
                return <Box display="flex" alignItems="center">
                    <Box sx={{ width: "50px", borderBottom: `2px solid ${item.color_code_dark}` }} />
                    <Typography variant="body2" color={item.color_code_dark} ml={1}>
                        {item.hint}
                    </Typography>
                </Box>
            })}
        </Box>
    );
};

interface PopupProps {
    section : any
    commonResult : any
    bucklingData : any
    getStatuColor : (buckling_status: string, bucklingSchema: BucklingStatus[]) => { status: string; badgeColor: string; textColor: string; state : string }
    displayForm : (coord: any) => void,
    displayPopup : (coord: any) => void,
    setTagsPopupOpen : (val: boolean) => void,
    bucklingSchema: BucklingStatus[]
    responseSchema: ResponseSchema[]
}

function CustomPopup({ section, commonResult, bucklingData, getStatuColor, displayForm, displayPopup, setTagsPopupOpen, bucklingSchema, responseSchema }: PopupProps) {
    const map = useMap(); // Get the map instance
    let recommend = false
    const [showTags, setShowTags] = useState(false);

    let coord: [number, number] | null = null;

    let bsm: any = ''
    let statusInfo = { status: 'undefined', badgeColor: '#6E6E6E', textColor: 'white', state: 'g1' }
    let pointId = ''
    let secId = ''
    let edgePoint = ''

    if (typeof section === 'object' && Object.keys(section).length > 0) {
        if (section.coordinates && section.coordinates.length > 0) {
            secId = `${section?.railwayEdgeId}`
            pointId = `${secId}:${section?.coordinates[0].lat}-${section?.coordinates[0].long}`
            const lat = section.coordinates[0]?.lat;
            const long = section.coordinates[0]?.long;

            if (lat !== undefined && long !== undefined) {
                coord = [lat, long];
            }
        }
        edgePoint = `${section.edgeName}.${section.index}`
    }


    useEffect(() => {
        setShowTags(false)
    }, [section])

    const allTags = useAllTags()

    const pointTag = allTags[pointId];
    const edgeTag = allTags[`${secId}:0-0`];

    const tag =
        pointTag || edgeTag
            ? {
                ...(pointTag ?? edgeTag),
                tags: Array.from(
                    new Set([
                        ...(edgeTag?.tags ?? []),
                        ...(pointTag?.tags ?? []),
                    ])
                ),
            }
            : null;


    const tags = tag?.tags ?? [];
    const hasTags = tags.length > 0;

    if (coord)
        if (typeof bucklingData[`${coord[0]},${coord[1]}`] === "object" &&
            bucklingData[`${coord[0]},${coord[1]}`] !== null &&
            Object.keys(bucklingData[`${coord[0]},${coord[1]}`] as object).length > 0) {
            let response = bucklingData[`${coord[0]},${coord[1]}`] as SectionData
            bsm = response.bsm ?? ' ';
            statusInfo = getStatuColor(response.buckling_status, bucklingSchema)
            if (response?.recommendations && (response?.recommendations).length > 0 ) {
                recommend = true
            } else {
                recommend = false
            }
        }
        let unit = ''
        if(responseSchema && responseSchema.length > 0) {
            const item = responseSchema.find(item => item.code === 'bsm')
            unit = item?.unit ?? ''
        }
        
    const isMobile = window.innerWidth < 768;

    useEffect(() => {
        if (coord) {
            const popupContainer = document.createElement("div");

            const root = createRoot(popupContainer);
            root.render(
                <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm max-w-[220px] sm:max-w-[320px]">
                    <div>
                        Selected Track Section: <span className="text-[#027034]">{edgePoint}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <Button
                            color="success"
                            sx={{ textTransform: "none"}}
                            variant="outlined"
                            onClick={() => displayForm(coord)}
                        >
                            Calculate Track Stability
                        </Button>

                        {bsm !== '' && <div className='mt-3'>
                            <span className="font-medium">Calculated BSM:</span>{" "}
                            <span className="text-[#6f0689] font-semibold">{bsm} {unit}</span> 
                        </div>}
                    </div>
                    
                    {/* Tags Section */}
                    <div className="border rounded-md p-2 bg-gray-50 mt-2 mb-1">
                        {/* Header Row */}
                        <div
                            className="flex items-center justify-between cursor-pointer text-purple-700"
                            onClick={() => setShowTags(prev => !prev)}
                        >
                            <span>
                                {hasTags ? `Tags (${tags.length})` : "Tag Details"}
                            </span>

                            <span className="text-xs">
                                {showTags ? <ExpandMore /> : <ExpandLess />}
                            </span>
                        </div>

                        {/* Expanded */}
                        {showTags && (
                            <div className="mt-2 flex flex-wrap gap-1 max-h-[120px] overflow-y-auto">
                                {hasTags ? (
                                    <>
                                        {tags.map((t: string, i: number) => (
                                            <span
                                                key={i}
                                                className="bg-purple-100 text-[#027034] text-sm px-2 py-1 rounded-full truncate max-w-[220px]"
                                                title={t}
                                            >
                                                {t}
                                            </span>
                                        ))}

                                        <button
                                            onClick={() => setTagsPopupOpen(true)}
                                            className="text-purple-700 underline text-xs"
                                        >
                                            <AddCircleOutline />
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setTagsPopupOpen(true)}
                                        className="text-[#6f0689] underline text-xs"
                                    >
                                        Add Tags
                                    </button>
                                )}

                            </div>
                        )}
                    </div>

                    {recommend && <><Button
                        variant='contained'
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            padding: "8px",
                            margin: '8px',
                            fontWeight: "bold",
                            color: "#8d42a8",
                            backgroundColor: 'white',
                            borderColor: "#8d42a8",
                            "&:hover": {
                                borderColor: "primary.dark",
                                backgroundColor: "action.hover",
                            },
                            maxHeight: '40px'
                        }}
                        onClick={() => displayPopup(coord)}
                        // className="p-0 text-left whitespace-nowrap"
                        style={{ font: "inherit" }}
                        endIcon={<Recommend sx={{
                            animation: "pulse 1s ease-in-out infinite",
                            "@keyframes pulse": {
                                "0%": { transform: "scale(1)" },
                                "50%": { transform: "scale(1.2)" }, // Grows
                                "100%": { transform: "scale(1)" },
                            },
                        }} />}
                    >
                        <Typography noWrap sx={{ fontSize: "0.79rem" }}>Recommendations</Typography>
                    </Button></>}

                    {/* Status Badge */}
                    <Box
                        className="inline-block  px-3 py-1 text-xs font-bold rounded-md shadow"
                        sx={{
                            backgroundColor: statusInfo.badgeColor,
                            color: statusInfo.textColor,
                        }}
                    >
                        {statusInfo.status}
                    </Box>
                </div>
            );
            const popup = L.popup({ offset: [0,-20], maxWidth: isMobile ? 260 : 500, minWidth: isMobile ? 180 : 250, autoPan: true }) // Offset to position it above the marker
                .setLatLng(coord)
                .setContent(popupContainer)
                .openOn(map);

            return () => {
                map.closePopup(popup);
            };
        }
    }, [map, coord]);

    return null; // This component does not render anything itself
}

export const TagDialogDetails = ({section , isGeoPoint, edgeId , edgeName}: {section: any, isGeoPoint: boolean, edgeId?: string, edgeName?: string}) => {
    const [inputValue, setInputValue] = useState("");
    const permanentState = useAppPermanentState();
    const id = edgeId ?? `${section?.railwayEdgeId}`
    const name = edgeName ?? section?.edgeName ?? ''
    const pointId = 
            (isGeoPoint && section?.coordinates && section?.coordinates.length > 0) ?
              `${id}:${section?.coordinates[0].lat}-${section?.coordinates[0].long}`
            : `${id}:0-0` 

    const savedTags = useTags(pointId)
    const savedSectionsTagId = isGeoPoint ? `${id}:0-0` : ''
    let savedSectionsTag : any= useTags(savedSectionsTagId) ?? null
    savedSectionsTag  = isGeoPoint ? savedSectionsTag : undefined

    const coord = 
        (isGeoPoint && section?.coordinates &&section?.coordinates.length > 0) ?
            [section?.coordinates[0].lat, section?.coordinates[0].long]
            : [0,0]

    if (!section && !section.railwayEdgeId) {
        return <></>
    }

    
    const handleAdd = () => {
        if (inputValue.trim() !== "") {
            const tagsValue = {
                edgeId: id ?? '',
                edgeName: name ?? '',
                index: 1,
                lat: coord[0],
                lng: coord[1],
                tag:  inputValue.trim(),
                coordIndex: 0,
                isGeoPoint: true
            }
            updateTags(tagsValue,permanentState)
            setInputValue("");
        }
    };

    const handleDelete = () => {
        DeleteTags(`${id}:${coord[0]}-${coord[1]}`, permanentState)
    }

    return (
        <Box sx={{ p: 2, minWidth: 300 }}>
            <Stack direction="row" spacing={1} mb={2}>
                <TextField
                    label="Enter text"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleAdd();
                    }}
                />
                <Button variant="outlined" onClick={handleAdd}>
                    Add
                </Button>
            </Stack>

            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Stack direction="row" gap={2}  flexWrap="wrap">
                    {savedTags?.tags?.map((tag, index) => (
                        <Chip
                            sx={{ marginTop: '5'}}
                            key={index}
                            label={tag}
                            color="success"
                        />
                    ))}
                </Stack>

                {savedTags && savedTags?.tags && (
                    <Button
                        color="error"
                        size="small"
                        onClick={() =>handleDelete()} 
                    >
                        <DeleteForever />
                    </Button>
                )}
            </Box>

            {savedSectionsTag && Object.keys(savedSectionsTag).length > 0 && <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitle2" color="text.secondary"> Tag for {name}</Typography>
                <Stack direction="row" gap={2}  flexWrap="wrap">
                    {savedSectionsTag && savedSectionsTag?.tags?.map((tag: any, index: any) => (
                        <Chip
                            sx={{ marginTop: '5'}}
                            key={index}
                            label={tag}
                            color="success"
                        />
                    ))}
                </Stack>
            </Box>}

        </Box>
    );
}

export default HeatMap;

const toRadians = (deg: number) => (deg * Math.PI) / 180;
const toDegrees = (rad: number) => (rad * 180) / Math.PI;

/**
 * Get a coordinate 10 meters from the starting point along the path toward ending.
 */
const getPointAlongPath = (start: [number, number], end: [number, number], distance: number = 10) : [number, number] => {
    const R = 6378137; // Earth’s radius in meters

    const [lat1, lng1] = [toRadians(start[0]), toRadians(start[1])];
    const [lat2, lng2] = [toRadians(end[0]), toRadians(end[1])];

    // Compute the bearing from start to end
    const dLng = lng2 - lng1;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    const bearing = Math.atan2(y, x);

    // Compute the new latitude
    const latNew = Math.asin(
        Math.sin(lat1) * Math.cos(distance / R) + Math.cos(lat1) * Math.sin(distance / R) * Math.cos(bearing)
    );

    // Compute the new longitude
    const lngNew =
        lng1 +
        Math.atan2(
            Math.sin(bearing) * Math.sin(distance / R) * Math.cos(lat1),
            Math.cos(distance / R) - Math.sin(lat1) * Math.sin(latNew)
        );

    return [toDegrees(latNew) , toDegrees(lngNew)] ;
};


